import React, { useState, useEffect } from 'react';
import { Alert, Typography, Tag, Button } from 'antd';
import { ClockCircleOutlined, FireOutlined, DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { useLocation } from '../contexts/LocationContext';

const { Text } = Typography;

interface UrgencyMessage {
  type: 'seasonal' | 'pricing' | 'incentive' | 'early-adopter';
  title: string;
  description: string;
  deadline?: Date;
  highlight?: string;
}

export const UrgencyBanner: React.FC = () => {
  const { state } = useLocation();
  const [urgencyMessage, setUrgencyMessage] = useState<UrgencyMessage | null>(null);

  useEffect(() => {
    calculateUrgency();
  }, [state]);

  const calculateUrgency = () => {
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const day = now.getDate();

    // Seasonal urgency (summer peak rates)
    if (month >= 5 && month <= 8) { // June-September
      const daysUntilPeak = month === 5 ? 30 - day : 0;
      setUrgencyMessage({
        type: 'seasonal',
        title: 'Summer Peak Rates Start Soon',
        description: `Optimize before peak summer rates hit. Users who start now save an average of $200+ during peak season.`,
        deadline: new Date(now.getFullYear(), 6, 1), // July 1
        highlight: 'Start optimizing before peak rates',
      });
      return;
    }

    // Q4 Tax Incentive urgency
    if (month >= 9 && month <= 11) { // October-December
      const daysUntilQ4End = month === 11 ? 31 - day : (month === 10 ? 30 - day : 90);
      setUrgencyMessage({
        type: 'incentive',
        title: 'Maximize Q4 Tax Incentives',
        description: `Energy efficiency improvements completed this quarter qualify for tax incentives. Start now to maximize benefits.`,
        deadline: new Date(now.getFullYear(), 11, 31), // Dec 31
        highlight: `${daysUntilQ4End} days left in Q4`,
      });
      return;
    }

    // Early adopter pricing (if applicable)
    const daysSinceLaunch = Math.floor((now.getTime() - new Date(2024, 0, 1).getTime()) / (1000 * 60 * 60 * 24));
    if (daysSinceLaunch < 90) {
      setUrgencyMessage({
        type: 'early-adopter',
        title: 'Early Adopter Pricing',
        description: `Lock in current rates. First 100 subscribers get lifetime 20% discount. ${100 - Math.min(100, daysSinceLaunch)} spots remaining.`,
        highlight: 'Limited spots available',
      });
      return;
    }

    // Default: General urgency
    setUrgencyMessage({
      type: 'pricing',
      title: 'Limited-Time Offer',
      description: 'Start optimizing today. Every day you wait costs money in wasted energy.',
      highlight: 'Start saving today',
    });
  };

  const getIcon = () => {
    switch (urgencyMessage?.type) {
      case 'seasonal':
        return <FireOutlined />;
      case 'incentive':
        return <DollarOutlined />;
      case 'early-adopter':
        return <ClockCircleOutlined />;
      default:
        return <CalendarOutlined />;
    }
  };

  const getAlertType = () => {
    switch (urgencyMessage?.type) {
      case 'seasonal':
        return 'warning';
      case 'incentive':
        return 'success';
      case 'early-adopter':
        return 'info';
      default:
        return 'info';
    }
  };

  const formatTimeRemaining = (deadline?: Date) => {
    if (!deadline) return null;
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return null;
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.floor(days / 7)} weeks`;
    return `${Math.floor(days / 30)} months`;
  };

  if (!urgencyMessage) {
    return null;
  }

  return (
    <Alert
      message={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <Text strong style={{ fontSize: '16px', marginRight: '8px' }}>
              {urgencyMessage.title}
            </Text>
            {urgencyMessage.deadline && (
              <Tag color="red" icon={<ClockCircleOutlined />} style={{ marginLeft: '8px' }}>
                {formatTimeRemaining(urgencyMessage.deadline)} left
              </Tag>
            )}
            {urgencyMessage.highlight && !urgencyMessage.deadline && (
              <Tag color="blue" style={{ marginLeft: '8px' }}>
                {urgencyMessage.highlight}
              </Tag>
            )}
          </div>
          <div style={{ marginTop: '8px', width: '100%' }}>
            <Text>{urgencyMessage.description}</Text>
          </div>
        </div>
      }
      type={getAlertType()}
      icon={getIcon()}
      showIcon
      style={{ marginBottom: '24px' }}
      action={
        <Button 
          type="primary" 
          size="small"
          onClick={() => {
            const element = document.getElementById('pricing-section');
            element?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Get Started
        </Button>
      }
    />
  );
};

export default UrgencyBanner;


